import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import SectionReveal from "./SectionReveal";
import WeaverLogo from "./WeaverLogo";
import ContactModal from "./ContactModal";

const LEVELS = [
  {
    level: "01",
    service: "Short-Form Social",
    time: "1–2 days per video",
    cost: "$500–$1,000 per video",
    scope:
      "Reels, TikToks, Shorts, cutdowns, and other social videos under ~90 seconds. Client footage or stock, captions, music, simple graphics.",
  },
  {
    level: "02",
    service: "Standard Video Editing",
    time: "3–5 days per 3 minutes of finished video",
    cost: "$1,500–$2,500 per 3-min video",
    scope:
      "Social media content, standard corporate videos, testimonials, event highlights. Clean cuts, transitions, stock music, simple graphics.",
  },
  {
    level: "03",
    service: "High-End Commercial & Broadcast",
    time: "5–7 days per minute of finished video",
    cost: "$2,500–$3,500 per finished minute",
    scope:
      "TV commercials, broadcast content, premium corporate videos, high-end marketing. Advanced editing, motion graphics, color grading.",
  },
  {
    level: "04",
    service: "Video + Animation Hybrid",
    time: "5–7 days per 30 seconds of finished video",
    cost: "$2,500–$3,500 per 30 seconds",
    scope:
      "Explainer videos with animated overlays, branded social content, enhanced commercials. Live footage combined with motion graphics.",
  },
  {
    level: "05",
    service: "Pure Animation",
    time: "7–10 days per 30 seconds, following storyboard approval*",
    cost: "$3,500–$5,000 per 30 seconds*",
    scope:
      "Motion graphics, character animation, animated explainers, custom illustration. *Pre-production (script, storyboard, style frames) typically adds 1–2 weeks, scoped at the same day rate.",
  },
  {
    level: "06",
    service: "Music Composition",
    time: "5 days per minute of produced music",
    cost: "$2,500 per produced minute",
    scope:
      "Bespoke music composed and produced for your content — custom scores, brand themes, soundtracks for any project above. Original work you own, with no stock licensing limits.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Brief",
    body: "Tell us what you want to make. We scope it in production days and confirm a firm timeline and quote before any work begins.",
  },
  {
    n: "02",
    title: "Produce",
    body: "Your project goes into production. Drafts are delivered through Vimeo.com for time-stamped feedback.",
  },
  {
    n: "03",
    title: "Deliver",
    body: "Approve the final cut and receive platform-ready exports. You own everything — final deliverables and project files.",
  },
];

const Eyebrow = ({ children, testId }) => (
  <span
    className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6"
    data-testid={testId}
  >
    {children}
  </span>
);

const Pricing = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="pricing-page"
    >
      {/* Minimal header */}
      <header className="w-full px-6 sm:px-12 py-6 sm:py-8 flex items-center justify-between border-b border-white/5">
        <Link to="/" data-testid="pricing-logo-link" className="focus:outline-none">
          <WeaverLogo size="md" data-testid="pricing-page-logo" />
        </Link>
        <Link
          to="/"
          data-testid="pricing-back-home"
          className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft
            size={14}
            className="transition-transform duration-500 group-hover:-translate-x-1"
          />
          Back
        </Link>
      </header>

      <main>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-20 sm:pt-28 pb-24 sm:pb-32">
          <div className="max-w-4xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6"
            >
              — Per Project Pricing
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-none text-white"
              data-testid="pricing-headline"
            >
              Simple pricing.
              <br />
              <span className="text-zinc-500">$500 a day.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 text-base sm:text-lg font-light text-zinc-400 tracking-wide leading-relaxed max-w-2xl"
              data-testid="pricing-intro"
            >
              Every project is scoped in production days at a flat{" "}
              <span className="text-white font-medium">$500 day rate</span> —
              no hourly billing, no surprise line items. Estimates begin when
              we have your footage, brief, and brand assets in hand, and we
              confirm a firm timeline and quote before any work begins.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12"
            >
              <button
                onClick={() => setContactOpen(true)}
                data-testid="pricing-hero-cta"
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300"
              >
                Make Contact
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </button>
            </motion.div>
          </div>
        </section>

        {/* Levels + costs */}
        <section className="border-t border-white/5 bg-[#0A0A0A] py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <SectionReveal>
              <Eyebrow testId="pricing-levels-eyebrow">01 — Production Levels</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
                Six levels. <span className="text-zinc-500">One day rate.</span>
              </h2>
            </SectionReveal>

            <div className="mt-16 grid gap-px bg-white/10 border border-white/10">
              {LEVELS.map((l, i) => (
                <SectionReveal
                  key={l.level}
                  delay={Math.min(i * 0.08, 0.4)}
                  className="bg-[#0A0A0A] p-8 sm:p-10 grid sm:grid-cols-12 gap-6 sm:gap-10"
                  data-testid={`pricing-level-${l.level}`}
                >
                  <div className="sm:col-span-1 text-2xl sm:text-3xl font-black text-zinc-700">
                    {l.level}
                  </div>
                  <div className="sm:col-span-3">
                    <h3 className="text-lg sm:text-xl font-bold uppercase tracking-tight text-white leading-snug">
                      {l.service}
                    </h3>
                    <div className="mt-3 text-sm font-medium text-zinc-200">
                      {l.cost}
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Typical Time
                    </div>
                    <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                      {l.time}
                    </p>
                  </div>
                  <div className="sm:col-span-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Examples &amp; Scope
                    </div>
                    <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                      {l.scope}
                    </p>
                  </div>
                </SectionReveal>
              ))}
            </div>

            <SectionReveal delay={0.1}>
              <div className="mt-10 bg-[#111] border border-white/10 p-8 sm:p-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  Brand Integration · Add 1–2 Days
                </div>
                <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                  Want your own brand graphics woven into the work — logos,
                  fonts, lower thirds, title cards, existing templates? Plan for
                  one to two additional days on your first project for ingest,
                  reconstruction, and bespoke design of your brand elements.
                  This setup only happens once: after your brand system is
                  built into our pipeline, every subsequent project uses it
                  with no added time.
                </p>
              </div>
            </SectionReveal>
          </div>
        </section>

        {/* How it works */}
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <SectionReveal>
              <Eyebrow testId="pricing-how-eyebrow">02 — How It Works</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
                Brief. <span className="text-zinc-500">Produce. Deliver.</span>
              </h2>
            </SectionReveal>
            <div className="mt-16 grid sm:grid-cols-3 gap-px bg-white/10">
              {STEPS.map((s, i) => (
                <SectionReveal
                  key={s.n}
                  delay={i * 0.1}
                  className="bg-[#050505] p-8 sm:p-10"
                  data-testid={`pricing-step-${s.n}`}
                >
                  <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-600">
                    {s.n}
                  </div>
                  <h3 className="mt-4 text-xl sm:text-2xl font-bold uppercase tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    {s.body}
                  </p>
                </SectionReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Subscription cross-sell */}
        <section className="border-t border-white/5 bg-[#0A0A0A] py-24 sm:py-32">
          <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
            <SectionReveal>
              <Eyebrow testId="pricing-crosssell-eyebrow">03 — Ongoing Needs?</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95] text-white">
                Subscribers get
                <br />
                <span className="text-zinc-500">a better deal</span>
              </h2>
              <p className="mt-8 text-base sm:text-lg font-light text-zinc-400 tracking-wide leading-relaxed max-w-2xl mx-auto">
                A full month of dedicated production — roughly 21 working days
                that would cost{" "}
                <span className="text-white font-medium">$10,500</span> at the
                day rate — runs subscribers{" "}
                <span className="text-white font-medium">$6,500 flat</span>,
                with unlimited revisions, collaborative scriptwriting, stock
                licensing, and platform-ready delivery included. Month-to-month;
                cancel anytime.
              </p>
            </SectionReveal>
            <SectionReveal delay={0.15}>
              <div className="mt-12 flex justify-center">
                <Link
                  to="/subscription"
                  data-testid="pricing-subscription-link"
                  className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-transparent border border-white/25 text-white font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-300"
                >
                  Explore Subscription
                  <ArrowUpRight
                    size={18}
                    className="transition-transform duration-500 group-hover:rotate-45"
                  />
                </Link>
              </div>
            </SectionReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 sm:py-36">
          <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
            <SectionReveal>
              <Eyebrow testId="pricing-cta-eyebrow">04 — Get Started</Eyebrow>
              <h2 className="text-4xl sm:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white">
                Tell us what
                <br />
                <span className="text-zinc-500">you want to make</span>
              </h2>
            </SectionReveal>
            <SectionReveal delay={0.15}>
              <div className="mt-14 flex justify-center">
                <button
                  onClick={() => setContactOpen(true)}
                  data-testid="pricing-footer-cta"
                  className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto px-10 py-5 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300"
                >
                  Make Contact
                  <ArrowUpRight
                    size={18}
                    className="transition-transform duration-500 group-hover:rotate-45"
                  />
                </button>
              </div>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">
                $500/day · Firm quote before work begins
              </p>
            </SectionReveal>
          </div>
        </section>
      </main>

      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 0,
            fontFamily: "Montserrat, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.05em",
          },
        }}
      />
    </div>
  );
};

export default Pricing;
